import { getBaseLayer, getNodeWithChildrenRecursive, getCurrentLayer, pasteImageFromClipboard, isClipboardImage } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { deleteNode, pasteNodeFromJSON } from "@builder/store/slices/BuilderSlice/builderSlice";
import { validateAndMergeJSX } from "@builder/store/slices/BuilderSlice/mergeJSXValidation";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import store from "@builder/store/store";
import LZString from "lz-string";


  
const _base64ToUtf8 = (b64: string): string => {
    const binary = atob(b64);
    const bytes  = Uint8Array.from(binary, ch => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  };

const _zipAndUtf8ToBase64 = (text: string): string => {
    return LZString.compressToBase64(text);
};

const _base64AndUnzipToUtf8 = (b64: string): string => {
    const decompressed = LZString.decompressFromBase64(b64);
    if (!decompressed) {
        throw new Error("Failed to decompress data");
    }
    return decompressed;
};
  


let fallbackMemory: string = "";

function effectifyNodeInDOM(id: string) {
    const domNode = document.getElementById(id);
    if (!domNode) return;
    //flash effect
    const effectDurationMS = 70;
    const originalFilter = domNode.style.filter;
    const originalTransition = domNode.style.transition;
    domNode.style.transition = `filter ${effectDurationMS}ms ease-in-out`;
    domNode.style.filter = "opacity(0.2) blur(2px)";
    setTimeout(() => {
        domNode.style.filter = originalFilter;
        setTimeout(() => {
            domNode.style.transition = originalTransition;
        }, effectDurationMS);
    }, effectDurationMS);
}

export const copySelectedNode = async ({zip = true}: {zip?: boolean} = {}) => {
    const baseLayer = getBaseLayer(store.getState().builder as BuilderState);
    const selectedNode = store.getState().builder.selectedNode;
    if (!selectedNode) return;
    const node = baseLayer.components[selectedNode];
    const parent = node.parent;
    if (!parent) return; // Can't duplicate root node
    const duplicatedNode = getNodeWithChildrenRecursive(baseLayer, node, parent || baseLayer.id);
    // Compress with lz-string and then encode to base64
    const json = zip ? _zipAndUtf8ToBase64(JSON.stringify(duplicatedNode)) : JSON.stringify(duplicatedNode);
    effectifyNodeInDOM(selectedNode);
    fallbackMemory = json;

    try {
        await navigator.clipboard.writeText(json);
    } catch (err) {
        console.error("Failed to write to clipboard, falling back to temporary memory: ", err);
    }
    return json;
};

export const cutSelectedNode = async () => {
    const selectedId = store.getState().builder.selectedNode;
    if (!selectedId) return;
    const json = await copySelectedNode();
    store.dispatch(deleteNode(selectedId));
    return json;
};

export const pasteSelectedNode = async () => {
    try {
        const clipboardImage = await isClipboardImage((navigator as any).clipboard);
        if (clipboardImage) {
            const handled = await pasteImageFromClipboard(clipboardImage);
            if (handled) return;
        }

        let dataToPaste = await navigator.clipboard.readText();
        if (!dataToPaste) {
            //fallback to temporary memory
            if (fallbackMemory) {
                dataToPaste = fallbackMemory;
            }
            else {
                console.error("No data to paste");
                return;
            }
        }

        if (dataToPaste.includes ("<")) {
            const currentLayerId = getCurrentLayer().layer.id;
            try {
                validateAndMergeJSX({
                    jsx: dataToPaste,
                    targetLayerId: currentLayerId,
                    targetElement: store.getState().builder.selectedNode || undefined,
                });
            } catch (error) {
                console.error("Failed to merge JSX into node:", error);
            }
        }
        else {
            let decodedData: string;
            try {
                decodedData = _base64AndUnzipToUtf8(dataToPaste);
                JSON.parse(decodedData);
            } catch (error) {
                try {
                    decodedData = _base64ToUtf8(dataToPaste);
                    JSON.parse(decodedData)
                } catch (fallbackError) {
                    console.error("Failed to decode clipboard data:", fallbackError);
                    return;
                }
            }
            store.dispatch(pasteNodeFromJSON(decodedData));
            return;
        }
    } catch (err) {
        console.error("Failed to read from clipboard, falling back to temporary memory: ", err);
    }

};

export const deleteSelectedNode = () => {
    const selectedId = store.getState().builder.selectedNode;
    if (!selectedId) return;
    store.dispatch(deleteNode(selectedId));
};
